import React, { useEffect, useRef, useState } from 'react';
import { DotIndicators } from './DotIndicators';
import { BugReportModal } from './BugReportModal';
import { ConciergeBar } from './ConciergeBar';
import {
  SECTION_LABELS,
  TOTAL_SECTIONS,
  HEADER_HEIGHT,
  ANIMATION_DURATION,
} from './constants';
import { BG_CARD } from '../legacy-ball/table-management/designTokens';
import { useAuth } from '../../contexts/AuthContext';

export interface HeaderPanelProps {
  currentIndex: number;
  sectionName: string;
  imageUrl: string; // resolved URL (admin-uploaded or default)
  isFirst: boolean;
  isLast: boolean;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  onDotClick: (index: number) => void;
  containerRef: React.RefObject<HTMLDivElement>;
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    width: '100%',
    userSelect: 'none',
  },
  panelContainer: {
    position: 'relative',
    width: '100%',
    height: `${HEADER_HEIGHT}px`,
    overflow: 'hidden',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: `transform ${ANIMATION_DURATION}ms ease`,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: '1.5rem',
    fontWeight: 700,
    textAlign: 'center' as const,
    padding: '0 48px',
    margin: 0,
    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255, 255, 255, 0.25)',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: '1.2rem',
    zIndex: 2,
    transition: 'opacity 0.2s ease',
  },
  arrowLeft: {
    left: '8px',
  },
  arrowRight: {
    right: '8px',
  },
  arrowDisabled: {
    opacity: 0.3,
    cursor: 'default',
  },
  logoutButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    border: '1.5px solid rgba(255, 255, 255, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: '1rem',
    zIndex: 3,
    lineHeight: 1,
    padding: 0,
  },
  bugReportButton: {
    position: 'absolute',
    top: '10px',
    right: '52px',
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    border: '1.5px solid rgba(255, 255, 255, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: '0.9rem',
    zIndex: 3,
    lineHeight: 1,
    padding: 0,
  } as React.CSSProperties,
  dotsContainer: {
    backgroundColor: BG_CARD,
    padding: '4px 0',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
};

export const HeaderPanel: React.FC<HeaderPanelProps> = ({
  currentIndex,
  sectionName,
  imageUrl,
  isFirst,
  isLast,
  onSwipeLeft,
  onSwipeRight,
  onDotClick,
  containerRef,
}) => {
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const prevIndexRef = useRef(currentIndex);
  const { logout } = useAuth();
  const [bugModalOpen, setBugModalOpen] = useState(false);

  useEffect(() => {
    if (currentIndex !== prevIndexRef.current) {
      setSlideDirection(currentIndex > prevIndexRef.current ? 'left' : 'right');
      prevIndexRef.current = currentIndex;

      // Reset slide direction after animation completes
      const timer = setTimeout(() => {
        setSlideDirection(null);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [currentIndex]);

  const getImageTransform = (): string => {
    if (slideDirection === 'left') return 'translateX(0)';
    if (slideDirection === 'right') return 'translateX(0)';
    return 'translateX(0)';
  };

  const handlePrevClick = () => {
    if (!isFirst) {
      onSwipeRight();
    }
  };

  const handleNextClick = () => {
    if (!isLast) {
      onSwipeLeft();
    }
  };

  const announcementText = `${sectionName} — section ${currentIndex + 1} of ${TOTAL_SECTIONS}`;

  return (
    <div style={styles.wrapper}>
      {/* Swipeable panel container */}
      <div ref={containerRef} style={{
        ...styles.panelContainer,
        backgroundImage: imageUrl ? `url(${imageUrl})` : `linear-gradient(135deg, #1a6b3c, #2dd4bf)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}>

        {/* Dark semi-transparent overlay for text contrast */}
        <div style={styles.overlay}>
          <h2 style={styles.sectionTitle}>{sectionName}</h2>
        </div>

        {/* Left arrow button */}
        <button
          style={{
            ...styles.arrowButton,
            ...styles.arrowLeft,
            ...(isFirst ? styles.arrowDisabled : {}),
          }}
          onClick={handlePrevClick}
          aria-label="Previous section"
          aria-disabled={isFirst ? 'true' : undefined}
          tabIndex={0}
        >
          ‹
        </button>

        {/* Right arrow button */}
        <button
          style={{
            ...styles.arrowButton,
            ...styles.arrowRight,
            ...(isLast ? styles.arrowDisabled : {}),
          }}
          onClick={handleNextClick}
          aria-label="Next section"
          aria-disabled={isLast ? 'true' : undefined}
          tabIndex={0}
        >
          ›
        </button>

        {/* Bug Report button — top-right corner (next to logout) */}
        <button
          style={styles.bugReportButton}
          onClick={() => setBugModalOpen(true)}
          aria-label="Report a bug"
          tabIndex={0}
        >
          🐛
        </button>

        {/* Logout button — top-right corner */}
        <button
          style={styles.logoutButton}
          onClick={() => { if (window.confirm('Log out?')) logout(); }}
          aria-label="Log out"
          tabIndex={0}
        >
          ⏻
        </button>
      </div>

      {/* Dot indicators */}
      <div style={styles.dotsContainer}>
        <DotIndicators
          totalDots={TOTAL_SECTIONS}
          activeIndex={currentIndex}
          onDotClick={onDotClick}
          sectionNames={[...SECTION_LABELS]}
        />
      </div>

      {/* AI Concierge — collapsible bar below dots */}
      <ConciergeBar />

      {/* Aria-live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" style={styles.srOnly as React.CSSProperties}>
        {announcementText}
      </div>

      {/* Bug Report Modal */}
      {bugModalOpen && <BugReportModal onClose={() => setBugModalOpen(false)} />}
    </div>
  );
};

export default HeaderPanel;
